import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';

import { actUpdatePageTitle } from 'store/ducks/nav/actions';
import MyNewButton from 'components/generals/buttons/MyNewButton';
import { ROWS_PER_PAGE } from 'components/generals/table/MyTable';
import MyInputFilter from 'components/generals/input/MyInputFilter';
import MyTableCell from 'components/generals/table/MyTableCell';
import MyTablePaginationActions from 'components/generals/table/MyTablePaginationActions';
import MySortableTableCell from 'components/generals/table/MySortableTableCell';

import { AppState } from 'store';
import {
  loadBoxes,
  loadBoxFile as loadBoxesFile,
} from 'store/ducks/generals/boxes/actions';
import { BoxesState } from 'store/ducks/generals/boxes/types';
import { useFilters } from 'hooks/filters';
import { formatColumnValue } from 'helpers/formatters';
import { Box, FormatOptions } from 'types';
import { boxesFilters, searchObject } from './utils';
import { useStyles } from './styles';
import ActionsButton from 'components/generals/buttons/actionsButton/actionsButton';
import { ROOT_PATH } from 'services/fileservice';
import { FileState } from 'store/ducks/generals/file/types';

const Boxes: FC = () => {
  const dispatch = useDispatch();
  const styles = useStyles();
  const history = useHistory();
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE[0]);
  const {
    sort,
    page,
    filterFields,
    genericFilterFields,
    handleGenericSearch,
    handleSort,
    handleSetValuePage,
  } = useFilters(boxesFilters);

  const { data: boxes, isLoading, total, hasError } = useSelector<
    AppState,
    BoxesState
  >(({ boxes }: AppState) => boxes);

  const { hasError: hasErrorFile } = useSelector<AppState, FileState>(
    ({ file }: AppState) => file,
  );

  const formatDate = useCallback(
    (value: string) =>
      formatColumnValue(FormatOptions.SIMPLE_DATE_WITH_TRACE, value),
    [],
  );

  const loadAllBoxes = useCallback(() => {
    dispatch(
      loadBoxes({
        sort,
        page: page + 1,
        limit: rowsPerPage,
        search: {
          $and: [...filterFields, { $or: [...genericFilterFields] }],
        },
      }),
    );
  }, [dispatch, sort, page, rowsPerPage, filterFields, genericFilterFields]);

  const loadBoxesXls = useCallback(() => {
    dispatch(
      loadBoxesFile(
        {
          join: [['plans']],
          sort,
          page: page + 1,
          limit: rowsPerPage,
          search: {
            $and: [...filterFields, { $or: [...genericFilterFields] }],
          },
        },
        ROOT_PATH.BOXES,
      ),
    );
  }, [dispatch, page, sort, rowsPerPage, genericFilterFields, filterFields]);

  const redirectToBox = (box: Box) =>
    history.push(`/admin/boxes/${box.id}`, { box });

  useEffect(() => {
    loadAllBoxes();
  }, [loadAllBoxes]);

  useEffect(() => {
    dispatch(actUpdatePageTitle('Boxes'));
  }, [dispatch]);

  const handleNewBoxes = () => {
    history.push(`/admin/boxes-new`);
  };

  return (
    <Grid container className={styles.containerMargin}>
      <Grid container className={styles.headerMargin}>
        <Grid item xs={3} sm={3} md={3} className={styles.searchFilter}>
          <MyInputFilter
            onChange={(event: any) => handleGenericSearch(event, searchObject)}
            placeholder={'Buscar'}
          />
        </Grid>
        <Grid container item xs justify="flex-end" alignItems="flex-end">
          <Grid item xs={2} xl={2}>
            <ActionsButton
              hasErrorFile={hasErrorFile}
              menuItems={[
                {
                  title: 'Adicionar',
                  action: handleNewBoxes,
                },
                {
                  title: 'Exportar planilha de boxes',
                  action: loadBoxesXls,
                },
              ]}
              buttonText="Ações"
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container direction="column" spacing={3}>
        <Grid item xs={12}>
          <Paper className={styles.paperTable} elevation={0}>
            <TableContainer>
              <Table className={styles.table} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <MySortableTableCell
                      onClick={() => handleSort('id')}
                      direction={sort.field === 'id' && sort.order}
                    >
                      Código
                    </MySortableTableCell>
                    <MySortableTableCell
                      onClick={() => handleSort('name')}
                      direction={sort.field === 'name' && sort.order}
                    >
                      Nome
                    </MySortableTableCell>
                    <MySortableTableCell
                      onClick={() => handleSort('sku')}
                      direction={sort.field === 'sku' && sort.order}
                    >
                      SKU
                    </MySortableTableCell>
                    <MySortableTableCell
                      onClick={() => handleSort('startDate')}
                      direction={sort.field === 'startDate' && sort.order}
                    >
                      Data início
                    </MySortableTableCell>
                    <MySortableTableCell
                      onClick={() => handleSort('endDate')}
                      direction={sort.field === 'endDate' && sort.order}
                    >
                      Data fim
                    </MySortableTableCell>
                    <MySortableTableCell
                      onClick={() => handleSort('isReveal')}
                      direction={sort.field === 'isReveal' && sort.order}
                    >
                      Box revelado ?
                    </MySortableTableCell>
                    <TableCell>Plano</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isLoading && !hasError && boxes
                    ? boxes.map(box => (
                        <TableRow
                          key={box.id}
                          className={styles.clickableArea}
                          onClick={() => redirectToBox(box)}
                        >
                          <MyTableCell>{box.id}</MyTableCell>
                          <MyTableCell>{box.name}</MyTableCell>
                          <MyTableCell>{box.sku}</MyTableCell>
                          <MyTableCell>{formatDate(box.startDate)}</MyTableCell>
                          <MyTableCell>{formatDate(box.endDate)}</MyTableCell>
                          <MyTableCell>
                            {box.isReveal ? 'Sim' : 'Não'}
                          </MyTableCell>
                          <MyTableCell>
                            {box.plans?.map(plan => plan.name).join(', ')}
                          </MyTableCell>
                        </TableRow>
                      ))
                    : null}
                </TableBody>
              </Table>
              <TablePagination
                ActionsComponent={MyTablePaginationActions}
                component="div"
                count={total}
                page={page}
                labelRowsPerPage="Itens por página"
                onChangePage={(_event: unknown, newPage: number) =>
                  handleSetValuePage(newPage)
                }
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={(event: ChangeEvent<HTMLInputElement>) => {
                  handleSetValuePage(0);
                  setRowsPerPage(parseInt(event.target.value, 10));
                }}
                rowsPerPageOptions={ROWS_PER_PAGE}
              />
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Boxes;
